import React from 'react';
import { styled } from '@linaria/react';
import { produce } from 'immer';

import { BREAKPOINTS } from '@/constants';

import Directory from './Directory';
import File from './File';
import { openFile, isFile } from './FileViewer.helpers';
import type {
  DirectoryInterface,
  FileGroup,
} from './FileViewer.types';

interface TogglerFns {
  handleToggleDirectory: (directorySlug: string) => void;
  handleSelectFile: (fileSlug: string) => void;
  isExpanded?: boolean;
}

function generateChildren(
  items: FileGroup,
  props: TogglerFns,
  slugPrefix = ''
) {
  return items.map((item) => {
    const slug = `${slugPrefix}/${item.name}`;

    if (isFile(item)) {
      return (
        <File
          key={slug}
          file={item}
          slug={slug}
          onClick={props.handleSelectFile}
          isExpanded={!!props.isExpanded}
        />
      );
    } else {
      return (
        <Directory
          key={slug}
          slug={slug}
          name={item.name}
          isExpanded={item.isExpanded}
          onClick={props.handleToggleDirectory}
        >
          {generateChildren(
            item.contents,
            { ...props, isExpanded: item.isExpanded },
            slug
          )}
        </Directory>
      );
    }
  });
}

interface Props {
  files: FileGroup;
  setFiles: React.Dispatch<React.SetStateAction<FileGroup>>;
}

function Sidebar({ files, setFiles }: Props) {
  function handleToggleDirectory(directorySlug: string) {
    const paths = directorySlug.slice(1).split('/');

    setFiles((f) => {
      return produce(f, (draftFiles) => {
        let reference: DirectoryInterface = {
          name: 'parent',
          type: 'directory',
          isExpanded: false,
          contents: draftFiles,
        };

        while (paths.length) {
          let currentPath = paths.shift();

          if (currentPath) {
            // @ts-ignore
            reference = reference.contents.find((item) => {
              return item.name === currentPath;
            });
          }
        }

        reference.isExpanded = !reference.isExpanded;
      });
    });
  }

  function handleSelectFile(fileSlug: string) {
    setFiles((f) => {
      return produce(f, (draftFiles) => {
        openFile(draftFiles, fileSlug);
      });
    });
  }

  const children = generateChildren(files, {
    handleToggleDirectory,
    handleSelectFile,
  });

  return <Wrapper>{children}</Wrapper>;
}

const Wrapper = styled.div`
  --row-height: 1.75rem;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 16px 0 12px;
  margin-top: -0.25rem;
  width: 16rem;
  font-size: 1rem;

  @media ${BREAKPOINTS.mdAndSmaller} {
    width: auto;
    min-height: 320px;
    padding: 0 24px;
  }
`;

export default Sidebar;
